/**
 * How this widget is mounted, which is a different question from which domain it speaks for.
 *
 * `embedded`: an embedding host imports the component and mounts it inside its own page, in its
 * own window, with no frame boundary in between.
 *
 * `iframe`: the standalone application is loaded by an iframe host, in a document and on an
 * origin of its own.
 *
 * The host declares this once, in its configuration. It cannot be sniffed reliably: a widget
 * mounted by an embedding host that happens to sit in a frame of its own would look identical to
 * the standalone application from the inside, and the two behave differently.
 */
export type ChatbotSurface = "embedded" | "iframe";

export const CHATBOT_SURFACES: readonly ChatbotSurface[] = ["embedded", "iframe"];

export function isChatbotSurface(value: unknown): value is ChatbotSurface {
    return typeof value === "string" && (CHATBOT_SURFACES as readonly string[]).includes(value);
}

/**
 * Whether the widget must place itself on screen (a fixed corner bubble for Floating, a docked
 * panel for Sidebar) instead of simply filling the element that contains it.
 *
 * An embedding host mounts the widget in a page that has no frame boundary around it, so nothing
 * outside is sizing or positioning it and it has to do that itself. An iframe host owns the
 * frame's size and position already, and the standalone application fills that frame edge to
 * edge.
 */
export function usesEmbeddedPositioning(surface: ChatbotSurface): boolean {
    return surface === "embedded";
}
